import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const PortalContext = createContext(null);

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
};

export const PortalProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('portal_access_token');
    if (token) {
      validateToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (token) => {
    try {
      const response = await api.get('/portal/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
    } catch (error) {
      localStorage.removeItem('portal_access_token');
      localStorage.removeItem('portal_refresh_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/portal/login', {
        email,
        password,
      });

      const { access_token, refresh_token, customer: customerData } = response.data;
      localStorage.setItem('portal_access_token', access_token);
      localStorage.setItem('portal_refresh_token', refresh_token);
      setCustomer(customerData);
      navigate('/portal/dashboard');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please check your credentials.',
      };
    }
  };

  const register = async (customerData) => {
    try {
      const response = await api.post('/portal/register', customerData);
      const { access_token, refresh_token, customer: newCustomer } = response.data;
      localStorage.setItem('portal_access_token', access_token);
      localStorage.setItem('portal_refresh_token', refresh_token);
      setCustomer(newCustomer);
      navigate('/portal/dashboard');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed. Please try again.',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('portal_access_token');
    localStorage.removeItem('portal_refresh_token');
    setCustomer(null);
    navigate('/portal/login');
  };

  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('portal_access_token');
      const response = await api.put('/portal/profile', profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data.customer);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update profile.',
      };
    }
  };

  const value = {
    customer,
    login,
    logout,
    register,
    updateProfile,
    loading,
    isAuthenticated: !!customer,
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};
